/**
 * Native UI primitives — match the existing CRM dashboard / leads page aesthetic.
 *
 * Use these instead of bespoke cards on every new page so the look stays
 * consistent with /dashboard and /leads.
 *
 * Reference: see PersonalDashboard.js KPICard / WidgetCard for the originals.
 */
import React from 'react';

// ─── Info icon — small (i) next to uppercase labels ─────────────────────────
export function InfoIcon({ tooltip }) {
  return (
    <span
      className="inline-flex align-middle ml-1 cursor-help"
      title={tooltip || ''}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
      aria-label={tooltip || 'info'}
    >
      <svg className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </span>
  );
}

// ─── Trend indicator — small ↑/↓ + number ───────────────────────────────────
export function TrendIndicator({ value, suffix = '', isPositiveGood = true }) {
  if (value === undefined || value === null) return null;
  const isPositive = value >= 0;
  const isGood = isPositiveGood ? isPositive : !isPositive;
  return (
    <span className={`inline-flex items-center text-xs font-medium ${isGood ? 'text-green-600' : 'text-red-500'}`}>
      {isPositive ? (
        <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
      {Math.abs(value)}{suffix}
    </span>
  );
}

// ─── Kebab menu icon (3 dots) ───────────────────────────────────────────────
function KebabIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
    </svg>
  );
}

// ─── KPI Card — matches the dashboard tiles ─────────────────────────────────
export function KPICard({ title, value, trend, unit, trendSuffix = '', onClick, tooltip, testId }) {
  const clickable = typeof onClick === 'function';
  const Wrapper = clickable ? 'button' : 'div';
  const wrapperProps = clickable
    ? {
        type: 'button',
        onClick,
        'data-testid': testId,
        'aria-label': `${title} — view details`,
        className:
          'group text-left w-full bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 transition-all cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-blue-300 dark:hover:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900',
      }
    : {
        'data-testid': testId,
        className:
          'bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-sm transition-shadow',
      };
  return (
    <Wrapper {...wrapperProps}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {title}
          {tooltip && <InfoIcon tooltip={tooltip} />}
        </span>
        {clickable ? (
          <svg
            className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors"
            fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        ) : (
          <span className="text-slate-400"><KebabIcon /></span>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</span>
        {trend !== undefined && <TrendIndicator value={trend} suffix={trendSuffix} />}
      </div>
      {unit && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{unit}</p>}
    </Wrapper>
  );
}

// ─── Widget Card — outer container with uppercase title bar ─────────────────
export function WidgetCard({ title, tooltip, children, className = '', headerRight = null }) {
  return (
    <div className={`bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 ${className}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {title}
          {tooltip && <InfoIcon tooltip={tooltip} />}
        </span>
        {headerRight !== null ? headerRight : (
          <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"><KebabIcon /></button>
        )}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// ─── Pill — matches the source/industry chips on Lead cards ────────────────
export function Pill({ children, tone = 'slate', className = '' }) {
  const TONES = {
    slate: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    rose: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${TONES[tone] || TONES.slate} ${className}`}>
      {children}
    </span>
  );
}

// ─── Primary CTA button — matches the black "Add Lead" button ──────────────
export function PrimaryButton({ children, onClick, type = 'button', className = '', icon = null, ...rest }) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 hover:bg-slate-800 text-white dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 transition-colors ${className}`}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

// ─── Tab strip — matches /leads Kanban|List toggle ─────────────────────────
export function TabStrip({ tabs, value, onChange, dataTestidPrefix = 'tab' }) {
  return (
    <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 gap-0.5 border border-slate-200 dark:border-slate-700">
      {tabs.map((t) => {
        const active = value === t.id;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-all ${
              active
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm font-medium'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
            data-testid={`${dataTestidPrefix}-${t.id}`}
          >
            {Icon && <Icon className="w-4 h-4" />}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
